import React, { useState } from 'react';
import '../styles/Sidebar.css';

interface Conversation {
  id: string;
  mode: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  pinned: boolean;
  messageCount: number;
}

interface Props {
  conversations: Conversation[];
  activeConversation: string | null;
  workspace: string | null;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onPinConversation: (id: string, pinned: boolean) => void;
  onDeleteConversation: (id: string) => void;
  onFolderPick: (handle: any, name: string) => void;
  loading: boolean;
}

const MODE_ICONS: Record<string, string> = {
  GOVERNANCE: '🎯', CAPITAL: '💰', LEGAL: '⚖️', TECHNOLOGY: '⚙️',
  BRAND: '🎨', OPERATIONS: '📋', MARKETING: '📢',
  GROWTH: '📈', PARTNERSHIPS: '🤝', CUSTOMER: '❤️',
};

export default function Sidebar({
  conversations, activeConversation, workspace,
  onSelectConversation, onNewChat,
  onPinConversation, onDeleteConversation, onFolderPick,
  loading
}: Props) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = conversations.filter(c =>
    c.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const pinned = filtered.filter(c => c.pinned);
  const recent = filtered.filter(c => !c.pinned);

  async function pickFolder() {
    if (!('showDirectoryPicker' in window)) {
      alert('Folder access requires Chrome or Edge 86+');
      return;
    }
    try {
      const handle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
      onFolderPick(handle, handle.name);
    } catch {
      // User cancelled — no action needed
    }
  }

  const displayWorkspace = workspace || null;

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <span className="sidebar-logo">COGENAI</span>
        <button onClick={onNewChat} disabled={loading} className="new-chat-button" title="New conversation">
          + New
        </button>
      </div>

      <div className="workspace-section">
        <button
          className={`workspace-button${displayWorkspace ? ' has-folder' : ''}`}
          onClick={pickFolder}
          title={displayWorkspace ? `Workspace: ${displayWorkspace} — click to change` : 'Pick a folder'}
        >
          <span className="workspace-icon">📁</span>
          <span className="workspace-label">{displayWorkspace || 'Open Folder…'}</span>
          {displayWorkspace && <span className="workspace-change">↺</span>}
        </button>
      </div>

      <div className="sidebar-search">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="sidebar-content">
        {pinned.length > 0 && (
          <div className="conversation-group">
            <div className="group-title">Pinned</div>
            {pinned.map(c => (
              <ConversationItem
                key={c.id}
                conversation={c}
                isActive={activeConversation === c.id}
                onSelect={() => onSelectConversation(c.id)}
                icon={MODE_ICONS[c.mode] || '🧠'}
                onPin={() => onPinConversation(c.id, false)}
                onDelete={() => onDeleteConversation(c.id)}
              />
            ))}
          </div>
        )}

        <div className="conversation-group">
          <div className="group-title">Recent</div>
          {recent.length > 0 ? (
            recent.map(c => (
              <ConversationItem
                key={c.id}
                conversation={c}
                isActive={activeConversation === c.id}
                onSelect={() => onSelectConversation(c.id)}
                icon={MODE_ICONS[c.mode] || '🧠'}
                onPin={() => onPinConversation(c.id, true)}
                onDelete={() => onDeleteConversation(c.id)}
              />
            ))
          ) : (
            <div className="empty-conversations">No conversations yet</div>
          )}
        </div>
      </div>

      <div className="sidebar-footer">
        <button className="settings-button" title="Settings">Settings</button>
      </div>
    </div>
  );
}

interface ItemProps {
  conversation: Conversation;
  isActive: boolean;
  onSelect: () => void;
  icon: string;
  onPin: () => void;
  onDelete: () => void;
}

function ConversationItem({ conversation, isActive, onSelect, icon, onPin, onDelete }: ItemProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`conversation-item ${isActive ? 'active' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button className="conv-main" onClick={onSelect}>
        <span className="conv-icon">{icon}</span>
        <div className="conv-text">
          <div className="conv-title">{conversation.title}</div>
          <div className="conv-meta">{conversation.mode} · {conversation.messageCount}msg</div>
        </div>
      </button>

      {hovered && (
        <div className="conv-actions">
          <button
            className={`conv-action ${conversation.pinned ? 'conv-pinned' : ''}`}
            onClick={e => { e.stopPropagation(); onPin(); }}
            title={conversation.pinned ? 'Unpin' : 'Pin'}
          >
            {conversation.pinned ? '📌' : '📎'}
          </button>
          <button
            className="conv-action conv-delete"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            title="Delete"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
